import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchTopAttractions } from 'actions/ExperienceActions'

interface UseTopAttractionsResult {
  attractions: Array<Experiences.Attraction>;
  fetching: boolean;
}

const BROAD_PLACE_TYPES: ReadonlyArray<App.PlaceType> = ['country', 'continent']

/** Returns true for country/continent or places missing geographic bounds (no boundingRadius), which rely on countryCode query. */
function isBroadPlace(place?: App.Place): boolean {
  if (!place) return false
  if (place.type && BROAD_PLACE_TYPES.includes(place.type)) return true
  if (!place.type && place.countryCode && !place.boundingRadius) return true
  return false
}

function useTopAttractions(place?: App.Place): UseTopAttractionsResult {
  const dispatch = useAppDispatch()
  const placeId = place?.id
  const placeName = place?.name
  const placeType = place?.type
  const placeCountryCode = place?.countryCode?.toUpperCase()
  const broad = isBroadPlace(place)

  const usePlaceQuery = !!placeId && !broad

  const key = usePlaceQuery
    ? `top-attractions-place-${placeId}`
    : placeCountryCode
      ? `top-attractions-country-${placeCountryCode}`
      : undefined

  const result = useAppSelector((state) =>
    key ? state.experience.topAttractions[key] : undefined,
  )

  useEffect(() => {
    if (usePlaceQuery) {
      dispatch(fetchTopAttractions({
        placeName,
        placeType,
        countryCode: placeCountryCode,
        lePlaceId: placeId,
      }))
    } else if (placeCountryCode) {
      dispatch(fetchTopAttractions({ countryCode: placeCountryCode }))
    }
  }, [key, usePlaceQuery, placeId, placeName, placeType, placeCountryCode, dispatch])

  const topLimit = result?.topAttractionsLimit ?? 5
  return {
    attractions: (result?.attractions ?? []).slice(0, topLimit),
    fetching: result?.fetching ?? (!!key && !result),
  }
}

export default useTopAttractions
